// The videos of the walkthrough new members get after signing up (apps/web Guide.tsx), one per
// language, set in Admin → Settings → New-member guide. Each language has a phone video (portrait,
// played full screen) and a desktop one (landscape, in the dialog); either may be left empty.
// Shared by the setting's check, the admin editor and the web player, so all three read a link
// the same way.
import type { Locale } from './i18n.js';

export const GUIDE_VIDEO_KINDS = ['phone', 'desktop'] as const;
export type GuideVideoKind = (typeof GUIDE_VIDEO_KINDS)[number];
export type GuideVideoLinks = Record<GuideVideoKind, string>;
/** Languages without an entry (or with empty links) get no video. */
export type GuideVideos = Partial<Record<Locale, Partial<GuideVideoLinks>>>;

/**
 * The library and video of a Bunny Stream link: the embed address
 * (https://iframe.mediadelivery.net/embed/<library>/<video>) or the direct play page
 * (https://player.mediadelivery.net/play/<library>/<video>), on either host. Null for anything
 * else, so a link can never point the player at another site.
 */
export function bunnyVideo(
  link: string,
): { origin: string; library: string; video: string } | null {
  if (link.length > 300) return null;
  try {
    const u = new URL(link);
    if (u.protocol !== 'https:' || !/^(iframe|player)\.mediadelivery\.net$/.test(u.hostname))
      return null;
    const m = u.pathname.match(
      /^\/(?:embed|play)\/(\d{1,12})\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/?$/i,
    );
    if (!m) return null;
    return { origin: u.origin, library: m[1]!, video: m[2]!.toLowerCase() };
  } catch {
    return null;
  }
}

/**
 * Bunny's player for a video link (bunnyVideo), as the guide embeds it: loaded but not started,
 * so the tap that opens the video can start it with sound; inline on iPhones; no loop. Its
 * origin is the only one the guide talks to (player.js messages). Null for any other link.
 */
export function bunnyEmbed(link: string): { src: string; origin: string } | null {
  const v = bunnyVideo(link);
  if (!v) return null;
  const q = new URLSearchParams({
    autoplay: 'false',
    preload: 'true',
    responsive: 'true',
    loop: 'false',
    muted: 'false',
    playsinline: 'true',
  });
  return { src: `${v.origin}/embed/${v.library}/${v.video}?${q}`, origin: v.origin };
}

/**
 * A language's videos as visitors get them: each link checked, null where there's none, and null
 * altogether when the language has neither. The player uses the other kind's video when its own
 * is missing (apps/web Guide.tsx), framed for that video's shape.
 */
export function guideVideoFor(
  videos: GuideVideos,
  locale: Locale,
): Record<GuideVideoKind, string | null> | null {
  const v = videos[locale];
  const ok = (link: string | undefined) => (link && bunnyVideo(link) ? link : null);
  const out = { phone: ok(v?.phone), desktop: ok(v?.desktop) };
  return out.phone || out.desktop ? out : null;
}
